import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, MapPin, Briefcase, Building, User, ChevronDown, X, Phone, Mail, Globe, Linkedin, GraduationCap, Calendar, ArrowRight, Home, Clock, Edit3, LogOut, IdCard } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { resolveFileUrl } from '../../utils/fileUrl';
import { toast } from 'sonner';
import ProfessionalEditProfileModal from '../../components/ProfessionalEditProfileModal';
import PaginationBar from '../../components/PaginationBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ProfessionalDirectoryPage = () => {
  const navigate = useNavigate();
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({ professions: [], states: [], nukhs: [], qualifications: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const PAGE_SIZE = 20;
  const [filters, setFilters] = useState({
    search: '',
    profession: '',
    state: '',
    city: '',
    nukh: ''
  });

  // Modal state for professional details
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Logged-in professional profile + edit modal state
  const [myProfile, setMyProfile] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Check if user is logged in (any user type)
  const isLoggedIn = () => {
    return !!(localStorage.getItem('professionalToken') || localStorage.getItem('familyToken') || localStorage.getItem('token'));
  };

  const isProfessionalLoggedIn = () => !!localStorage.getItem('professionalToken');

  // Get any available auth token
  const getAuthToken = () => {
    return localStorage.getItem('professionalToken') || localStorage.getItem('familyToken') || localStorage.getItem('token');
  };

  // Fetch logged-in professional's own profile
  const fetchMyProfile = async () => {
    const token = localStorage.getItem('professionalToken');
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/professionals/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMyProfile(data);
      } else if (res.status === 401) {
        // Token invalid - clear it
        localStorage.removeItem('professionalToken');
        localStorage.removeItem('professionalId');
        localStorage.removeItem('professionalName');
        localStorage.removeItem('professionalEmail');
      }
    } catch (e) {
      console.error('Error fetching my profile:', e);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('professionalToken');
    localStorage.removeItem('professionalId');
    localStorage.removeItem('professionalName');
    localStorage.removeItem('professionalEmail');
    setMyProfile(null);
    toast.success('Logged out successfully');
    navigate('/professional/login');
  };

  // Fetch full professional details for logged-in users
  const fetchFullDetails = async (profId) => {
    const token = getAuthToken();
    if (!token) return null;

    try {
      const response = await fetch(`${BACKEND_URL}/api/professionals/directory/${profId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Error fetching full details:', error);
    }
    return null;
  };

  const openProfessionalModal = async (professional) => {
    setSelectedProfessional(professional);
    setModalLoading(true);

    if (isLoggedIn()) {
      const fullDetails = await fetchFullDetails(professional.id);
      if (fullDetails) {
        setSelectedProfessional(fullDetails);
      }
    }
    setModalLoading(false);
  };

  const closeProfessionalModal = () => {
    setSelectedProfessional(null);
  };

  useEffect(() => {
    fetchOptions();
    fetchProfessionals();
    fetchMyProfile();
  }, []);

  const fetchOptions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/professionals/options`);
      if (response.ok) {
        const data = await response.json();
        setOptions(data);
      }
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchProfessionals = async (page = currentPage) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.profession) params.append('profession', filters.profession);
      if (filters.state) params.append('state', filters.state);
      if (filters.city) params.append('city', filters.city);
      if (filters.nukh) params.append('nukh', filters.nukh);
      params.append('page', page);
      params.append('limit', PAGE_SIZE);

      const response = await fetch(`${BACKEND_URL}/api/professionals/directory?${params}`);
      if (response.ok) {
        const data = await response.json();
        setProfessionals(data.professionals || []);
        setTotalCount(data.total || 0);
        setTotalPages(data.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching professionals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchProfessionals(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ search: '', profession: '', state: '', city: '', nukh: '' });
    setCurrentPage(1);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchProfessionals(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.profession, filters.state, filters.nukh]);

  const getProfessionDisplay = (professional) => {
    if (professional.profession === 'Other / अन्य' && professional.professionOther) {
      return professional.professionOther;
    }
    return professional.profession?.split(' / ')[0] || professional.profession;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="gradient-orange py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <a href="/" className="hover:text-white">Home</a>
              <span>›</span>
              <a href="/professionals" className="hover:text-white">Professionals</a>
              <span>›</span>
              <span>Directory</span>
            </div>

            {isProfessionalLoggedIn() && myProfile && (
              <div className="flex items-center gap-2 flex-wrap">
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/15 rounded-full text-white text-sm">
                  <User size={14} />
                  <span>{myProfile.fullName}</span>
                  {myProfile.professionalId && (
                    <span className="text-white/80 text-xs">({myProfile.professionalId})</span>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={() => setShowEditModal(true)}
                  className="bg-white text-primary hover:bg-gray-100"
                  data-testid="edit-my-profile-btn"
                >
                  <Edit3 size={14} className="mr-1" /> Edit My Profile
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleLogout}
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  data-testid="prof-directory-logout-btn"
                >
                  <LogOut size={14} className="mr-1" /> Logout
                </Button>
              </div>
            )}
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Professional Directory
          </h1>
          <p className="text-white/90 mb-6">
            Find and connect with professionals from our community
          </p>

          {/* Status banner for logged-in professional with pending/rejected status */}
          {isProfessionalLoggedIn() && myProfile?.status === 'pending' && (
            <div className="mb-4 p-3 bg-yellow-50/95 border border-yellow-300 rounded-lg flex items-center gap-3 max-w-2xl" data-testid="prof-directory-pending-banner">
              <IdCard className="w-5 h-5 text-yellow-700 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-900">Your profile is under review</p>
                <p className="text-xs text-yellow-800">Your registration is pending admin approval. Once approved, your profile will be visible in the directory.</p>
              </div>
            </div>
          )}
          {isProfessionalLoggedIn() && myProfile?.status === 'rejected' && (
            <div className="mb-4 p-3 bg-red-50/95 border border-red-300 rounded-lg flex items-center gap-3 max-w-2xl" data-testid="prof-directory-rejected-banner">
              <X className="w-5 h-5 text-red-700 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-900">Registration Rejected</p>
                <p className="text-xs text-red-800">Please edit your profile and save to resubmit for approval.</p>
              </div>
            </div>
          )}

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex gap-2 max-w-2xl">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Search by name, profession, specialization..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 h-12 bg-white"
              />
            </div>
            <Button type="submit" className="h-12 px-6 bg-secondary hover:bg-secondary/90">
              Search
            </Button>
          </form>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter Toggle & Active Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter size={16} />
            Filters
            <ChevronDown size={16} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </Button>

          {(filters.profession || filters.state || filters.nukh) && (
            <>
              {filters.profession && (
                <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  {filters.profession.split(' / ')[0]}
                  <X size={14} className="cursor-pointer" onClick={() => handleFilterChange('profession', '')} />
                </span>
              )}
              {filters.state && (
                <span className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  {filters.state}
                  <X size={14} className="cursor-pointer" onClick={() => handleFilterChange('state', '')} />
                </span>
              )}
              {filters.nukh && (
                <span className="bg-green-50 text-green-600 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  {filters.nukh}
                  <X size={14} className="cursor-pointer" onClick={() => handleFilterChange('nukh', '')} />
                </span>
              )}
              <button onClick={clearFilters} className="text-sm text-gray-500 hover:text-primary">
                Clear all
              </button>
            </>
          )}

          <span className="ml-auto text-sm text-gray-500">
            {professionals.length} of {totalCount} professional{totalCount !== 1 ? 's' : ''} found
          </span>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-100"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Profession</label>
                <select
                  value={filters.profession}
                  onChange={(e) => handleFilterChange('profession', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                >
                  <option value="">All Professions</option>
                  {options.professions.map((prof, idx) => (
                    <option key={idx} value={prof}>{prof}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <select
                  value={filters.state}
                  onChange={(e) => handleFilterChange('state', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                >
                  <option value="">All States</option>
                  {options.states.map((state, idx) => (
                    <option key={idx} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <Input
                  type="text"
                  placeholder="Enter city"
                  value={filters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nukh</label>
                <select
                  value={filters.nukh}
                  onChange={(e) => handleFilterChange('nukh', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                >
                  <option value="">All Nukhs</option>
                  {options.nukhs.map((nukh, idx) => (
                    <option key={idx} value={nukh}>{nukh}</option>
                  ))}
                </select>
              </div>
            </div>
          </motion.div>
        )}

        {/* Results Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : professionals.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl">
            <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Professionals Found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your search or filters</p>
            <Link to="/professional/register">
              <Button className="bg-primary hover:bg-primary/90">
                Register as Professional
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {professionals.map((professional, index) => (
              <motion.div
                key={professional.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {professional.profilePhoto ? (
                      <img
                        src={resolveFileUrl(professional.profilePhoto)}
                        alt={professional.fullName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-8 h-8 text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{professional.fullName}</h3>
                    <p className="text-primary font-medium text-sm">
                      {getProfessionDisplay(professional)}
                    </p>
                    {professional.specialization && (
                      <p className="text-gray-500 text-sm">{professional.specialization}</p>
                    )}
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  {professional.organization && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Building size={14} className="flex-shrink-0" />
                      <span className="truncate">{professional.organization}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin size={14} className="flex-shrink-0" />
                    <span>{professional.city}, {professional.state}</span>
                  </div>
                  {professional.nukh && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{professional.nukh}</span>
                    </div>
                  )}
                </div>

                {professional.bio && (
                  <p className="mt-4 text-sm text-gray-600 line-clamp-2">{professional.bio}</p>
                )}

                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  {professional.experienceYears && (
                    <span className="text-xs text-gray-500">
                      {professional.experienceYears}+ years experience
                    </span>
                  )}
                  {professional.qualification && (
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                      {professional.qualification}
                    </span>
                  )}
                </div>

                {/* View Details Link */}
                <div className="mt-3 pt-3 border-t border-dashed">
                  <button
                    onClick={() => openProfessionalModal(professional)}
                    className="w-full text-center text-primary hover:text-primary-600 text-sm font-medium flex items-center justify-center gap-1 py-2.5 min-h-[40px] hover:bg-primary/5 rounded transition-colors"
                    data-testid={`view-details-${professional.id}`}
                  >
                    Click here to view details
                    <ArrowRight size={14} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* CTA for non-logged in users */}
        {!isLoggedIn() && (
          <div className="mt-12 bg-gradient-to-r from-primary to-primary-600 rounded-2xl p-8 text-center text-white">
            <h3 className="text-2xl font-bold mb-4">Want to see contact details?</h3>
            <p className="mb-6 text-white/90">Login to view full profiles and connect with professionals</p>
            <div className="flex justify-center gap-4">
              <Link to="/professional/login">
                <Button className="bg-white text-primary hover:bg-gray-100">
                  Login
                </Button>
              </Link>
              <Link to="/professional/register">
                <Button variant="outline" className="border-white text-white hover:bg-white/10">
                  Register
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div data-testid="prof-directory-pagination" className="mt-8 mb-4">
          <PaginationBar
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(p) => { setCurrentPage(p); fetchProfessionals(p); }}
            testIdPrefix="prof-directory"
          />
        </div>
      )}

      {/* Professional Details Modal */}
      <AnimatePresence>
        {selectedProfessional && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={closeProfessionalModal}
            data-testid="professional-details-modal-backdrop"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="professional-details-modal"
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-primary to-primary-600 text-white p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                      {selectedProfessional.profilePhoto ? (
                        <img
                          src={resolveFileUrl(selectedProfessional.profilePhoto)}
                          alt={selectedProfessional.fullName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-8 h-8 text-white" />
                      )}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">{selectedProfessional.fullName}</h2>
                      {selectedProfessional.professionalId && (
                        <p className="text-white/90 text-sm">ID: {selectedProfessional.professionalId}</p>
                      )}
                      <p className="text-white/80 text-sm">{getProfessionDisplay(selectedProfessional)}</p>
                    </div>
                  </div>
                  <button
                    onClick={closeProfessionalModal}
                    className="text-white/80 hover:text-white p-1"
                    data-testid="close-modal-btn"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                {modalLoading && (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                )}

                {isLoggedIn() ? (
                  <>
                    {/* Personal Details (logged-in only) */}
                    {(selectedProfessional.fatherName || selectedProfessional.motherName || selectedProfessional.dateOfBirth || selectedProfessional.nukh) && (
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <User className="w-5 h-5 text-primary" />
                          Personal Details
                        </h3>
                        <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                          {selectedProfessional.fatherName && (
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Father's Name</p>
                              <p className="text-gray-900 font-medium">{selectedProfessional.fatherName}</p>
                            </div>
                          )}
                          {selectedProfessional.motherName && (
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Mother's / Husband's Name</p>
                              <p className="text-gray-900 font-medium">{selectedProfessional.motherName}</p>
                            </div>
                          )}
                          {selectedProfessional.nukh && (
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Nukh</p>
                              <p className="text-gray-900 font-medium">{selectedProfessional.nukh}</p>
                            </div>
                          )}
                          {selectedProfessional.dateOfBirth && (
                            <div className="flex items-start gap-2">
                              <Calendar size={14} className="text-gray-400 mt-1" />
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Date of Birth</p>
                                <p className="text-gray-900 font-medium">{selectedProfessional.dateOfBirth}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Full Professional Info (logged-in) */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-primary" />
                        Professional Details
                      </h3>
                      <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Profession</p>
                          <p className="text-gray-900 font-medium">{getProfessionDisplay(selectedProfessional)}</p>
                        </div>
                        {selectedProfessional.specialization && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Specialization</p>
                            <p className="text-gray-900 font-medium">{selectedProfessional.specialization}</p>
                          </div>
                        )}
                        {selectedProfessional.qualification && (
                          <div className="flex items-start gap-2">
                            <GraduationCap size={14} className="text-gray-400 mt-1" />
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Qualification</p>
                              <p className="text-gray-900 font-medium">{selectedProfessional.qualification}</p>
                            </div>
                          </div>
                        )}
                        {selectedProfessional.experienceYears && (
                          <div className="flex items-start gap-2">
                            <Clock size={14} className="text-gray-400 mt-1" />
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Experience</p>
                              <p className="text-gray-900 font-medium">{selectedProfessional.experienceYears} years</p>
                            </div>
                          </div>
                        )}
                        {selectedProfessional.organization && (
                          <div className="flex items-start gap-2">
                            <Building size={14} className="text-gray-400 mt-1" />
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Organization</p>
                              <p className="text-gray-900 font-medium">{selectedProfessional.organization}</p>
                            </div>
                          </div>
                        )}
                        {selectedProfessional.designation && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Designation</p>
                            <p className="text-gray-900 font-medium">{selectedProfessional.designation}</p>
                          </div>
                        )}
                        {selectedProfessional.officeAddress && (
                          <div className="col-span-2">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Office Address</p>
                            <p className="text-gray-900 font-medium">{selectedProfessional.officeAddress}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Full Location (logged-in) */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-primary" />
                        Location
                      </h3>
                      <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedProfessional.country && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Country</p>
                            <p className="text-gray-900 font-medium">{selectedProfessional.country}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">State</p>
                          <p className="text-gray-900 font-medium">{selectedProfessional.state || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">City</p>
                          <p className="text-gray-900 font-medium">{selectedProfessional.city || '—'}</p>
                        </div>
                        {selectedProfessional.currentPlace && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Area / Locality</p>
                            <p className="text-gray-900 font-medium">{selectedProfessional.currentPlace}</p>
                          </div>
                        )}
                        {selectedProfessional.pincode && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Pincode</p>
                            <p className="text-gray-900 font-medium">{selectedProfessional.pincode}</p>
                          </div>
                        )}
                        {selectedProfessional.address && (
                          <div className="col-span-2">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Address</p>
                            <p className="text-gray-900 font-medium">{selectedProfessional.address}</p>
                          </div>
                        )}
                        {(selectedProfessional.nativeVillage || selectedProfessional.nativePlace) && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                              <Home size={12} /> Native Village / Place
                            </p>
                            <p className="text-gray-900 font-medium">{selectedProfessional.nativeVillage || selectedProfessional.nativePlace}</p>
                          </div>
                        )}
                        {selectedProfessional.nativeCity && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Native City</p>
                            <p className="text-gray-900 font-medium">{selectedProfessional.nativeCity}</p>
                          </div>
                        )}
                        {selectedProfessional.nativeState && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Native State</p>
                            <p className="text-gray-900 font-medium">{selectedProfessional.nativeState}</p>
                          </div>
                        )}
                        {selectedProfessional.nativeCountry && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Native Country</p>
                            <p className="text-gray-900 font-medium">{selectedProfessional.nativeCountry}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Bio (logged-in) */}
                    {selectedProfessional.bio && (
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">About</h3>
                        <p className="text-gray-700 bg-gray-50 rounded-xl p-4">{selectedProfessional.bio}</p>
                      </div>
                    )}

                    {/* Contact Info (logged-in) */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Phone className="w-5 h-5 text-primary" />
                        Contact Information
                      </h3>
                      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                        {selectedProfessional.email && (
                          <div className="flex items-center gap-3">
                            <Mail size={16} className="text-gray-400 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Email</p>
                              <a href={`mailto:${selectedProfessional.email}`} className="text-primary hover:underline font-medium">
                                {selectedProfessional.email}
                              </a>
                            </div>
                          </div>
                        )}
                        {selectedProfessional.phone && (
                          <div className="flex items-center gap-3">
                            <Phone size={16} className="text-gray-400 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Phone</p>
                              <a href={`tel:${selectedProfessional.phone}`} className="text-gray-900 font-medium">
                                {selectedProfessional.phone}
                              </a>
                            </div>
                          </div>
                        )}
                        {selectedProfessional.whatsapp && (
                          <div className="flex items-center gap-3">
                            <Phone size={16} className="text-green-500 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">WhatsApp</p>
                              <p className="text-gray-900 font-medium">{selectedProfessional.whatsapp}</p>
                            </div>
                          </div>
                        )}
                        {selectedProfessional.website && (
                          <div className="flex items-center gap-3">
                            <Globe size={16} className="text-gray-400 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Website</p>
                              <a href={selectedProfessional.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                                {selectedProfessional.website}
                              </a>
                            </div>
                          </div>
                        )}
                        {selectedProfessional.linkedIn && (
                          <div className="flex items-center gap-3">
                            <Linkedin size={16} className="text-blue-600 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">LinkedIn</p>
                              <a href={selectedProfessional.linkedIn} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                                {selectedProfessional.linkedIn}
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Additional Info from Applicant */}
                    {selectedProfessional.additionalInfo && (
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Additional Information / Feedback</h3>
                        <p className="text-gray-700 bg-blue-50 rounded-xl p-4 whitespace-pre-wrap">{selectedProfessional.additionalInfo}</p>
                      </div>
                    )}

                    {/* Registration / Conclave Info */}
                    <div className="mb-6 bg-gray-50 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Interested in Conclave 2026</p>
                        <p className="text-gray-900 font-medium">{selectedProfessional.interestedInConclave2026 ? 'Yes' : 'No'}</p>
                      </div>
                      {selectedProfessional.registrationDate && (
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Registered On</p>
                          <p className="text-gray-900 font-medium">
                            {new Date(selectedProfessional.registrationDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* LIMITED VIEW - Non-logged-in users */}
                    {/* Basic profession only */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-primary" />
                        Professional Details
                      </h3>
                      <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Profession</p>
                          <p className="text-gray-900 font-medium">{getProfessionDisplay(selectedProfessional)}</p>
                        </div>
                        {selectedProfessional.specialization && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Specialization</p>
                            <p className="text-gray-900 font-medium">{selectedProfessional.specialization}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Basic location only */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-primary" />
                        Location
                      </h3>
                      <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">City</p>
                          <p className="text-gray-900 font-medium">{selectedProfessional.city}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">State</p>
                          <p className="text-gray-900 font-medium">{selectedProfessional.state}</p>
                        </div>
                      </div>
                    </div>

                    {/* Login prompt */}
                    <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-6 text-center" data-testid="login-prompt-section">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <User className="w-6 h-6 text-primary" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Want to see full profile?</h4>
                      <p className="text-gray-600 text-sm mb-4">Login to view contact details, qualification, experience, organization and more</p>
                      <div className="flex justify-center gap-3">
                        <Link to="/auth">
                          <Button size="sm" className="bg-primary hover:bg-primary/90">
                            Login
                          </Button>
                        </Link>
                        <Link to="/professional/register">
                          <Button size="sm" variant="outline">
                            Register
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Modal Footer */}
              <div className="border-t p-4 bg-gray-50">
                <Button
                  onClick={closeProfessionalModal}
                  className="w-full bg-primary hover:bg-primary/90"
                  data-testid="close-modal-footer-btn"
                >
                  Close
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit My Profile Modal */}
      <ProfessionalEditProfileModal
        open={showEditModal}
        profile={myProfile}
        options={options}
        onClose={() => setShowEditModal(false)}
        onSaved={(updated) => setMyProfile(updated)}
      />
    </div>
  );
};

export default ProfessionalDirectoryPage;
